import React, { useRef, useState, useCallback, useEffect } from 'react';
import { CONSONANT_MAP, getVowelVariants } from '../geezData';
import * as tf from '@tensorflow/tfjs';

// Model and label paths - use CDN in production, local in development
const isProduction = process.env.NODE_ENV === 'production';
const CDN_BASE = 'https://assets.fidelkeys.app';
const LOCAL_BASE = process.env.PUBLIC_URL || '';

const MODEL_URL = isProduction ? `${CDN_BASE}/ml/web_model/model.json` : `${LOCAL_BASE}/ml/web_model/model.json`;
const LABELS_URL = isProduction ? `${CDN_BASE}/ml/web_model/labels.json` : `${LOCAL_BASE}/ml/web_model/labels.json`;

const getThemeColor = (variableName, fallback) => {
  if (typeof window === 'undefined') return fallback;
  return window.getComputedStyle(document.documentElement).getPropertyValue(variableName).trim() || fallback;
};

export default function DrawingPad({ onCharRecognized, active }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [strokes, setStrokes] = useState([]);
  const [currentStroke, setCurrentStroke] = useState(null);
  const [predictions, setPredictions] = useState([]); // [{ char, confidence }, ...] top 3 from ML
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // ML model state
  const modelRef = useRef(null);
  const labelsRef = useRef(null);
  const [modelStatus, setModelStatus] = useState('loading'); // loading | ready | error
  const [modelError, setModelError] = useState(null);
  const [isPredicting, setIsPredicting] = useState(false);

  // Hidden offscreen canvas used to render the user's strokes on a WHITE background
  // with BLACK ink (matching the dataset the model was trained on) and scaled to 32×32.
  const workCanvasRef = useRef(null);

  const canvasWidth = 280;
  const canvasHeight = 280; // Square — the Ge'ez model expects a square 32×32 input

  // ===================================================================
  // Load TensorFlow.js model + labels on mount
  // ===================================================================
  useEffect(() => {
    let cancelled = false;
    async function initModel() {
      if (!tf) {
        setModelStatus('error');
        setModelError('TensorFlow.js not loaded');
        return;
      }
      try {
        const model = await tf.loadLayersModel(MODEL_URL);
        const resp = await fetch(LABELS_URL);
        if (!resp.ok) throw new Error(`Labels fetch failed (${resp.status})`);
        const labels = await resp.json();
        if (cancelled) return;
        modelRef.current = model;
        labelsRef.current = labels;
        setModelStatus('ready');
      } catch (err) {
        if (cancelled) return;
        console.error('[DrawingPad] Model load failed:', err);
        setModelStatus('error');
        setModelError(err.message || 'Failed to load model');
      }
    }
    initModel();
    return () => { cancelled = true; };
  }, []);

  // ===================================================================
  // Visual drawing (unchanged from original)
  // ===================================================================
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    const gridColor = getThemeColor('--border', '#2a2a3e');
    const notchColor = getThemeColor('--border-light', '#4a4a6e');
    const strokeColor = getThemeColor('--accent', '#00d4aa');
    const strokeGlowColor = getThemeColor('--accent-glow', 'rgba(0, 212, 170, 0.22)');

    // Subtle grid background
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 0.5;
    for (let i = 0; i < canvasWidth; i += 40) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, canvasHeight);
      ctx.stroke();
    }
    for (let i = 0; i < canvasHeight; i += 40) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(canvasWidth, i);
      ctx.stroke();
    }

    // Corner notch marks for Ge'ez reference lines
    ctx.strokeStyle = notchColor;
    ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(10, 10 + i * 8);
      ctx.lineTo(10 + (i % 2 ? 12 : 8), 10 + i * 8);
      ctx.stroke();
    }

    // Drawn strokes
    strokes.forEach(stroke => drawStrokePath(ctx, stroke, strokeColor, strokeGlowColor));
    if (currentStroke) {
      drawStrokePath(ctx, currentStroke, strokeColor, strokeGlowColor, true);
    }
  }, [strokes, currentStroke, canvasWidth, canvasHeight]);

  const drawStrokePath = (ctx, stroke, color, glowColor, animated) => {
    if (!stroke || stroke.length === 0) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(stroke[0].x, stroke[0].y);

    if (stroke.length < 3) {
      stroke.forEach(p => ctx.lineTo(p.x, p.y));
    } else {
      // Smooth quadratic curve
      for (let i = 1; i < stroke.length - 2; i++) {
        const xc = (stroke[i].x + stroke[i + 1].x) / 2;
        const yc = (stroke[i].y + stroke[i + 1].y) / 2;
        ctx.quadraticCurveTo(stroke[i].x, stroke[i].y, xc, yc);
      }
      ctx.quadraticCurveTo(
        stroke[stroke.length - 2].x,
        stroke[stroke.length - 2].y,
        stroke[stroke.length - 1].x,
        stroke[stroke.length - 1].y
      );
    }
    ctx.stroke();

    if (animated) {
      ctx.save();
      ctx.strokeStyle = glowColor;
      ctx.lineWidth = 8;
      ctx.globalAlpha = 0.18;
      ctx.stroke();
      ctx.restore();
    }
  };

  const getCanvasCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (!active) return;
    const coords = getCanvasCoords(e);
    setIsDrawing(true);
    setCurrentStroke([coords]);
    setPredictions([]);
    setSelectedPrediction(null);
  }, [active, getCanvasCoords]);

  const handlePointerMove = useCallback((e) => {
    if (!isDrawing || !active) return;
    const coords = getCanvasCoords(e);
    setCurrentStroke(prev => {
      if (!prev) return null;
      // Throttle: only add point if moved at least 3px
      const last = prev[prev.length - 1];
      const dist = Math.sqrt((coords.x - last.x) ** 2 + (coords.y - last.y) ** 2);
      if (dist < 3) return prev;
      return [...prev, coords];
    });
  }, [isDrawing, active, getCanvasCoords]);

  const handlePointerUp = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (currentStroke && currentStroke.length >= 3) {
      setStrokes(prev => [...prev, currentStroke]);
      setCurrentStroke(null);
    } else {
      setCurrentStroke(null);
    }
  }, [isDrawing, currentStroke]);

  // ===================================================================
  // ML prediction pipeline
  // ===================================================================

  // Render all current strokes onto the work canvas (white bg, black ink)
  const renderStrokesToWorkCanvas = useCallback(() => {
    const c = workCanvasRef.current;
    if (!c) return null;
    const ctx = c.getContext('2d');

    // White background (matches the dataset preprocessing)
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, c.width, c.height);

    // Black ink, thick stroke (proportional to the original 16px brush in a 280×280 canvas)
    ctx.strokeStyle = 'black';
    ctx.fillStyle = 'black';
    ctx.lineWidth = 10;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Render each stroke as a smooth path
    [...strokes, ...(currentStroke ? [currentStroke] : [])].forEach(stroke => {
      if (!stroke || stroke.length === 0) return;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      if (stroke.length < 3) {
        stroke.forEach(p => ctx.lineTo(p.x, p.y));
      } else {
        for (let i = 1; i < stroke.length - 2; i++) {
          const xc = (stroke[i].x + stroke[i + 1].x) / 2;
          const yc = (stroke[i].y + stroke[i + 1].y) / 2;
          ctx.quadraticCurveTo(stroke[i].x, stroke[i].y, xc, yc);
        }
        ctx.quadraticCurveTo(
          stroke[stroke.length - 2].x,
          stroke[stroke.length - 2].y,
          stroke[stroke.length - 1].x,
          stroke[stroke.length - 1].y
        );
      }
      ctx.stroke();
    });

    return ctx;
  }, [strokes, currentStroke]);

  // Find tightest bounding box of "ink" (non-white) pixels on a canvas
  const getBoundingBox = (ctx, w, h) => {
    const imgData = ctx.getImageData(0, 0, w, h);
    const data = imgData.data;
    let minX = w, minY = h, maxX = 0, maxY = 0;
    let found = false;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = (y * w + x) * 4;
        const r = data[idx], g = data[idx + 1], b = data[idx + 2];
        if (r < 240 || g < 240 || b < 240) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
          found = true;
        }
      }
    }
    return found ? { minX, minY, maxX, maxY } : null;
  };

  // Run the full ML pipeline on the current drawing
  const runPrediction = useCallback(async () => {
    if (!modelRef.current || !labelsRef.current) {
      setModelError('Model not loaded yet');
      return;
    }
    if (strokes.length === 0 && !currentStroke) {
      setPredictions([]);
      setSelectedPrediction(null);
      return;
    }
    setIsPredicting(true);
    setPredictions([]);
    setSelectedPrediction(null);

    try {
      // Make sure the work canvas exists and matches expected size
      if (!workCanvasRef.current) {
        workCanvasRef.current = document.createElement('canvas');
        workCanvasRef.current.width = canvasWidth;
        workCanvasRef.current.height = canvasHeight;
      }
      const workCanvas = workCanvasRef.current;

      // Step 1: render strokes onto the white-bg work canvas
      const workCtx = renderStrokesToWorkCanvas();
      if (!workCtx) {
        setIsPredicting(false);
        return;
      }

      // Step 2: find bounding box of drawn content
      const box = getBoundingBox(workCtx, workCanvas.width, workCanvas.height);
      if (!box) {
        setIsPredicting(false);
        return;
      }

      // Step 3: crop with 15% padding to a square
      const width = box.maxX - box.minX;
      const height = box.maxY - box.minY;
      const padding = Math.max(width, height) * 0.15;
      const size = Math.max(width, height) + padding * 2;
      const centerX = box.minX + width / 2;
      const centerY = box.minY + height / 2;
      const sourceX = centerX - size / 2;
      const sourceY = centerY - size / 2;

      // Step 4: scale the crop to a 32×32 canvas
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 32;
      tempCanvas.height = 32;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.fillStyle = 'white';
      tempCtx.fillRect(0, 0, 32, 32);
      tempCtx.drawImage(
        workCanvas,
        sourceX, sourceY, size, size,
        0, 0, 32, 32
      );

      // Step 5: build the model input tensor and predict
      const probabilities = tf.tidy(() => {
        // 1-channel grayscale
        let img = tf.browser.fromPixels(tempCanvas, 1);
        // Normalize AND invert: bg=0, ink=1 (matches the training data prep)
        img = tf.scalar(1.0).sub(img.div(tf.scalar(255.0)));
        // Add batch dim → [1, 32, 32, 1]
        const batched = img.expandDims(0);
        const prediction = modelRef.current.predict(batched);
        return prediction.dataSync();
      });

      // Step 6: get top 3 predictions
      const labels = labelsRef.current;
      const topK = Array.from(probabilities)
        .map((prob, idx) => ({ char: labels[idx], confidence: prob }))
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 3);

      setPredictions(topK);
      setSelectedPrediction(topK[0]);
    } catch (err) {
      console.error('[DrawingPad] Prediction failed:', err);
      setModelError(err.message || 'Prediction failed');
    } finally {
      setIsPredicting(false);
    }
  }, [strokes, currentStroke, renderStrokesToWorkCanvas, canvasWidth, canvasHeight]);

  const handleInterpretDrawing = useCallback(() => {
    if (strokes.length === 0 && !currentStroke) return;
    runPrediction();
  }, [strokes, currentStroke, runPrediction]);

  const handleClear = useCallback(() => {
    setStrokes([]);
    setCurrentStroke(null);
    setPredictions([]);
    setSelectedPrediction(null);
  }, []);

  const handleUndo = useCallback(() => {
    setStrokes(prev => prev.slice(0, -1));
    setPredictions([]);
    setSelectedPrediction(null);
  }, []);

  const handleAddToText = useCallback(() => {
    if (selectedPrediction) {
      onCharRecognized(selectedPrediction.char);
    }
  }, [selectedPrediction, onCharRecognized]);

  const isModelReady = modelStatus === 'ready';
  const hasResults = predictions.length > 0;

  // Render the model status pill in the header
  const modelStatusBadge = (() => {
    if (modelStatus === 'loading') {
      return (
        <span className="model-badge model-loading" title="Loading TensorFlow.js model…">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="badge-icon spin-animation">
            <line x1="12" y1="2" x2="12" y2="6"/>
            <line x1="12" y1="18" x2="12" y2="22"/>
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
            <line x1="2" y1="12" x2="6" y2="12"/>
            <line x1="18" y1="12" x2="22" y2="12"/>
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
          </svg>
          Loading model
        </span>
      );
    }
    if (modelStatus === 'error') {
      return (
        <span className="model-badge model-error" title={modelError || 'Model failed to load'}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="badge-icon">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          Model unavailable
        </span>
      );
    }
    return (
      <span className="model-badge model-ready" title="TensorFlow.js model loaded">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="badge-icon">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        ML ready
      </span>
    );
  })();

  return (
    <div className={`drawing-pad ${active ? 'active' : ''}`} ref={containerRef}>
      <div className="drawing-header">
        <span className="drawing-title">Draw Ge'ez</span>
        <div className="drawing-actions">
          <button onClick={handleUndo} disabled={strokes.length === 0} className="draw-btn">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="btn-icon">
              <path d="M3 7v6h6"/>
              <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>
            </svg>
            Undo
          </button>
          <button onClick={handleClear} disabled={strokes.length === 0 && !currentStroke} className="draw-btn">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="btn-icon">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            Clear
          </button>
          <button
            onClick={handleInterpretDrawing}
            disabled={!isModelReady || (strokes.length === 0 && !currentStroke) || isPredicting}
            className="draw-btn interpret-btn"
            title={isModelReady ? 'Run the neural network on your drawing' : 'Model is still loading…'}
          >
            {isPredicting ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="btn-icon spin-animation">
                  <line x1="12" y1="2" x2="12" y2="6"/>
                  <line x1="12" y1="18" x2="12" y2="22"/>
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
                  <line x1="2" y1="12" x2="6" y2="12"/>
                  <line x1="18" y1="12" x2="22" y2="12"/>
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
                </svg>
                Predicting…
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="btn-icon">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                Interpret & Add
              </>
            )}
          </button>
        </div>
      </div>
      {modelStatusBadge}

      <canvas
        ref={canvasRef}
        width={canvasWidth}
        height={canvasHeight}
        className={`drawing-canvas ${active ? 'drawing-canvas-active' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />

      {isPredicting && (
        <div className="drawing-hint">Running model inference…</div>
      )}

      {hasResults && !isPredicting && (
        <div className="drawing-matches">
          <span className="matches-label">Top predictions — pick one:</span>
          <div className="matches-list">
            {predictions.map((p, i) => (
              <button
                key={`${p.char}-${i}`}
                className={`match-btn ${selectedPrediction?.char === p.char ? 'match-selected' : ''}`}
                onClick={() => setSelectedPrediction(p)}
                title={`Confidence: ${(p.confidence * 100).toFixed(1)}%`}
              >
                <span className="match-char">{p.char}</span>
                <span className="match-vowel">{(p.confidence * 100).toFixed(0)}%</span>
              </button>
            ))}
          </div>
          <button
            className="add-to-text-btn"
            onClick={handleAddToText}
            disabled={!selectedPrediction}
            title="Add selected character to text area"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="btn-icon">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add "{selectedPrediction?.char || ''}" to Text
          </button>
        </div>
      )}

      <div className="drawing-hint">
        {active ? (
          isModelReady ? (
            <>
              Draw a character
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="drawing-hint-icon">
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </>
          ) : (
            'Draw a character (model loading…)'
          )
        ) : (
          'Click to start drawing'
        )}
      </div>
    </div>
  );
}